import { Agent } from "@atproto/api";
import { BrowserOAuthClient } from "@atproto/oauth-client-browser";
import { API_BASE, api } from "./api.js";

export const COLLECTIONS = {
  profile: "app.pliego.actor.profile",
  material: "app.pliego.feed.material",
  rating: "app.pliego.feed.rating",
  save: "app.pliego.feed.save",
  follow: "app.pliego.graph.follow",
  catalogEntry: "app.pliego.catalog.entry"
};

const scope = [
  "atproto",
  `repo:${COLLECTIONS.profile}`,
  `repo:${COLLECTIONS.material}`,
  `repo:${COLLECTIONS.rating}`,
  `repo:${COLLECTIONS.save}`,
  `repo:${COLLECTIONS.follow}`,
  `repo:${COLLECTIONS.catalogEntry}`,
  "blob:*/*"
].join(" ");

const origin = window.location.origin;
const loopback = /^http:\/\/(127\.0\.0\.1|\[::1\])(?::\d+)?$/.test(origin);
const clientMetadata = loopback ? undefined : {
  client_id: `${origin}/oauth-client-metadata.json`,
  client_name: "Pliego",
  client_uri: origin,
  logo_uri: `${origin}/pliego-mark.svg`,
  tos_uri: `${origin}/condiciones`,
  policy_uri: `${origin}/privacidad`,
  redirect_uris: [`${origin}/`],
  scope,
  grant_types: ["authorization_code", "refresh_token"],
  response_types: ["code"],
  token_endpoint_auth_method: "none",
  application_type: "web",
  dpop_bound_access_tokens: true
};

export const oauthClient = new BrowserOAuthClient({
  clientMetadata,
  handleResolver: API_BASE,
  responseMode: "query"
});

let initialization;

export async function initSession() {
  initialization ||= oauthClient.init();
  const result = await initialization;
  return result?.session || null;
}

export async function startSignIn(identifier) {
  await oauthClient.signInRedirect(identifier, { state: window.location.pathname, ui_locales: "es" });
}

export async function revokeSession(did) {
  await oauthClient.revoke(did);
}

export function agentFor(session) {
  return new Agent(session);
}

export async function publishProfile(agent, profile, avatarFile) {
  let avatar;
  if (avatarFile) {
    if (avatarFile.size > 2_000_000) throw new Error("La foto puede ocupar como máximo 2 MB.");
    const uploaded = await agent.uploadBlob(avatarFile, { encoding: avatarFile.type || "image/jpeg" });
    avatar = uploaded.data.blob;
  }
  const record = {
    $type: COLLECTIONS.profile,
    displayName: profile.displayName.trim(),
    description: profile.description.trim(),
    regionCode: profile.regionCode,
    programId: profile.programId,
    courseLabel: profile.courseLabel.trim(),
    subjectIds: profile.subjectIds,
    createdAt: new Date().toISOString(),
    ...(profile.institutionId ? { institutionId: profile.institutionId } : {}),
    ...(avatar ? { avatar } : {})
  };
  const result = await agent.com.atproto.repo.putRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.profile,
    rkey: "self",
    validate: false,
    record
  });
  await indexRecord(result.data.uri);
  return result.data;
}

export async function publishMaterial(agent, file, fields, onProgress, replacing = null) {
  if (!file || (file.type && file.type !== "application/pdf")) throw new Error("Elige un archivo PDF.");
  if (file.size > 100 * 1024 * 1024) throw new Error("El PDF puede ocupar como máximo 100 MB.");
  onProgress?.("Leyendo el contenido para que se pueda buscar…");
  const metadata = await extractPDFMetadata(file);
  onProgress?.("Subiendo el PDF a tu PDS…");
  const uploaded = await agent.uploadBlob(file, { encoding: "application/pdf" });
  onProgress?.("Publicando el pliego…");
  const record = {
    $type: COLLECTIONS.material,
    materialId: crypto.randomUUID(),
    title: fields.title.trim(),
    description: fields.description.trim(),
    documentType: fields.documentType,
    programId: fields.programId,
    courseLabel: fields.courseLabel.trim(),
    academicYear: fields.academicYear.trim(),
    rightsBasis: fields.rightsBasis,
    originalFilename: file.name,
    document: uploaded.data.blob,
    pageCount: metadata.pageCount,
    searchText: metadata.searchText,
    versionNumber: (replacing?.versionNumber || 0) + 1,
    tags: fields.tags.split(",").map(tag => tag.trim().toLowerCase()).filter(Boolean).slice(0, 12),
    createdAt: new Date().toISOString(),
    ...(replacing ? { replaces: { uri: replacing.uri, cid: replacing.cid } } : {}),
    ...(fields.subjectId ? { subjectId: fields.subjectId } : {}),
    ...(fields.institutionId ? { institutionId: fields.institutionId } : {}),
    ...(fields.licenseName ? { licenseName: fields.licenseName.trim() } : {}),
    ...(fields.licenseURL ? { licenseUrl: fields.licenseURL.trim() } : {})
  };
  const result = await agent.com.atproto.repo.createRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.material,
    validate: false,
    record
  });
  onProgress?.("Añadiéndolo al feed…");
  await indexRecord(result.data.uri);
  return { ...result.data, materialId: record.materialId };
}

export async function publishMaterialVersion(agent, file, material, onProgress) {
  return publishMaterial(agent, file, {
    title: material.title,
    description: material.description || "",
    documentType: material.documentType,
    programId: material.program.id,
    subjectId: material.subject?.id || "",
    institutionId: material.institution?.id || "",
    courseLabel: material.courseLabel || "",
    academicYear: material.academicYear || "",
    rightsBasis: material.rightsBasis,
    licenseName: material.licenseName || "",
    licenseURL: material.licenseURL || "",
    tags: (material.tags || []).join(", ")
  }, onProgress, material);
}

export async function exportRepository(agent) {
  const identity = await api(`/v1/identity/resolve?identifier=${encodeURIComponent(agent.assertDid)}`);
  const response = await fetch(`${identity.pdsURL}/xrpc/com.atproto.sync.getRepo?did=${encodeURIComponent(agent.assertDid)}`, {
    headers: { Accept: "application/vnd.ipld.car" }
  });
  if (!response.ok) throw new Error("Tu servidor no ha podido preparar la copia de la cuenta.");
  const files = { "repository.car": new Uint8Array(await response.arrayBuffer()) };
  let cursor;
  let blobCount = 0;
  do {
    const url = new URL("/xrpc/com.atproto.sync.listBlobs", identity.pdsURL);
    url.searchParams.set("did", agent.assertDid);
    url.searchParams.set("limit", "500");
    if (cursor) url.searchParams.set("cursor", cursor);
    const listResponse = await fetch(url);
    if (!listResponse.ok) throw new Error("El servidor no ha podido enumerar todos tus archivos.");
    const list = await listResponse.json();
    for (const rawCID of list.cids || []) {
      const cid = String(rawCID);
      const blobURL = new URL("/xrpc/com.atproto.sync.getBlob", identity.pdsURL);
      blobURL.searchParams.set("did", agent.assertDid);
      blobURL.searchParams.set("cid", cid);
      const blobResponse = await fetch(blobURL);
      if (!blobResponse.ok) throw new Error(`No se ha podido incluir el archivo ${cid}.`);
      files[`blobs/${cid}`] = new Uint8Array(await blobResponse.arrayBuffer());
      blobCount += 1;
    }
    cursor = list.cursor;
  } while (cursor);
  files["manifest.json"] = new TextEncoder().encode(JSON.stringify({
    format: "Pliego AT Protocol account export",
    did: agent.assertDid,
    handle: identity.handle,
    pdsURL: identity.pdsURL,
    exportedAt: new Date().toISOString(),
    blobCount
  }, null, 2));
  const { zipSync } = await import("fflate");
  return new Blob([zipSync(files, { level: 0 })], { type: "application/zip" });
}

export async function photosToPDF(files) {
  const { PDFDocument } = await import("pdf-lib");
  const pdf = await PDFDocument.create();
  for (const file of Array.from(files || [])) {
    const bytes = await file.arrayBuffer();
    const image = file.type === "image/png" ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
    const { width, height } = image.scale(1);
    const page = pdf.addPage([width, height]);
    page.drawImage(image, { x: 0, y: 0, width, height });
  }
  const bytes = await pdf.save();
  return new File([bytes], `Escaneo Pliego ${new Date().toLocaleDateString("es").replaceAll("/", "-")}.pdf`, { type: "application/pdf" });
}

async function extractPDFMetadata(file) {
  try {
    const [{ GlobalWorkerOptions, getDocument }, workerModule] = await Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url")
    ]);
    GlobalWorkerOptions.workerSrc = workerModule.default;
    const data = await file.arrayBuffer();
    const task = getDocument({ data });
    const pdf = await task.promise;
    const parts = [];
    let length = 0;
    for (let number = 1; number <= pdf.numPages && length < 48_000; number += 1) {
      const page = await pdf.getPage(number);
      const content = await page.getTextContent();
      const text = content.items.map(item => item.str || "").join(" ").replace(/\s+/g, " ").trim();
      if (text) {
        parts.push(text);
        length += text.length + 1;
      }
      page.cleanup();
    }
    const pageCount = pdf.numPages;
    await pdf.destroy();
    return { pageCount, searchText: parts.join(" ").slice(0, 48_000) };
  } catch {
    // A protected or unusual PDF can still be shared; only its internal text
    // will be unavailable to the search index.
    return { pageCount: 0, searchText: "" };
  }
}

export async function updateProfile(agent, profile, avatarFile, program) {
  const response = await agent.com.atproto.repo.listRecords({
    repo: agent.assertDid,
    collection: COLLECTIONS.profile,
    limit: 1
  });
  const current = response.data.records[0]?.value;
  if (!current) throw new Error("No se ha encontrado tu perfil publicado.");

  let avatar;
  if (avatarFile) {
    if (avatarFile.size > 2_000_000) throw new Error("La foto puede ocupar como máximo 2 MB.");
    const uploaded = await agent.uploadBlob(avatarFile, { encoding: avatarFile.type || "image/jpeg" });
    avatar = uploaded.data.blob;
  }

  const record = {
    ...current,
    $type: COLLECTIONS.profile,
    displayName: profile.displayName.trim(),
    description: profile.description.trim(),
    regionCode: profile.regionCode,
    programId: profile.programId,
    courseLabel: profile.courseLabel.trim(),
    subjectIds: profile.subjectIds,
    ...(avatar ? { avatar } : {})
  };
  if (profile.institutionId) record.institutionId = profile.institutionId;
  else delete record.institutionId;
  if (program?.id?.startsWith("community-")) {
    record.customProgramName = program.name;
    record.customProgramKind = program.kind;
  } else {
    delete record.customProgramName;
    delete record.customProgramKind;
  }

  const result = await agent.com.atproto.repo.putRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.profile,
    rkey: "self",
    validate: false,
    record
  });
  await indexRecord(result.data.uri);
  return result.data;
}

export async function createCatalogEntry(agent, fields) {
  let details = {};
  if (fields.entityType === "institution") {
    details = {
      regionCode: fields.regionCode,
      institutionKind: fields.institutionKind,
      ...(fields.province?.trim() ? { province: fields.province.trim() } : {}),
      ...(fields.city?.trim() ? { city: fields.city.trim() } : {})
    };
  } else if (fields.entityType === "program") {
    details = {
      programKind: fields.programKind,
      ...(fields.levelLabel?.trim() ? { levelLabel: fields.levelLabel.trim() } : {}),
      ...(fields.institutionId ? { institutionId: fields.institutionId } : {})
    };
  } else if (fields.entityType === "course") {
    details = {
      programId: fields.programId,
      ...(fields.sortOrder ? { sortOrder: Number.parseInt(fields.sortOrder, 10) || 0 } : {})
    };
  } else if (fields.entityType === "subject") {
    details = {
      programId: fields.programId,
      ...(fields.courseId ? { courseId: fields.courseId } : {}),
      ...(fields.courseLabel?.trim() ? { courseLabel: fields.courseLabel.trim() } : {}),
      ...(fields.semester?.trim() ? { semester: fields.semester.trim() } : {})
    };
  }
  const record = {
    $type: COLLECTIONS.catalogEntry,
    entryId: `community-${fields.entityType}-${crypto.randomUUID()}`,
    entityType: fields.entityType,
    name: fields.name.trim(),
    createdAt: new Date().toISOString(),
    ...details
  };
  const result = await agent.com.atproto.repo.createRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.catalogEntry,
    validate: false,
    record
  });
  await indexRecord(result.data.uri);
  return result.data;
}

export async function setRating(agent, material, stars) {
  const existing = await findMatching(agent, COLLECTIONS.rating, value => value.subject?.uri === material.uri);
  const record = {
    $type: COLLECTIONS.rating,
    subject: { uri: material.uri, cid: material.cid },
    halfStars: Math.max(1, Math.min(10, Math.round(stars * 2))),
    createdAt: new Date().toISOString()
  };
  const result = existing
    ? await agent.com.atproto.repo.putRecord({
      repo: agent.assertDid,
      collection: COLLECTIONS.rating,
      rkey: existing.uri.split("/").at(-1),
      validate: false,
      record
    })
    : await agent.com.atproto.repo.createRecord({
      repo: agent.assertDid,
      collection: COLLECTIONS.rating,
      validate: false,
      record
    });
  await indexRecord(result.data.uri);
}

export async function toggleSave(agent, material, shouldSave) {
  await removeMatching(agent, COLLECTIONS.save, value => value.subject?.uri === material.uri);
  if (!shouldSave) return;
  const result = await agent.com.atproto.repo.createRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.save,
    validate: false,
    record: {
      $type: COLLECTIONS.save,
      subject: { uri: material.uri, cid: material.cid },
      createdAt: new Date().toISOString()
    }
  });
  await indexRecord(result.data.uri);
}

export async function toggleFollow(agent, subjectType, subject, shouldFollow) {
  const normalizedSubject = subjectType === "tag"
    ? String(subject).trim().replace(/^#+/, "").replace(/\s+/g, " ").toLocaleLowerCase("es")
    : subject;
  await removeMatching(agent, COLLECTIONS.follow, value => value.subjectType === subjectType && value.subject === normalizedSubject);
  if (!shouldFollow) return;
  const result = await agent.com.atproto.repo.createRecord({
    repo: agent.assertDid,
    collection: COLLECTIONS.follow,
    validate: false,
    record: { $type: COLLECTIONS.follow, subjectType, subject: normalizedSubject, createdAt: new Date().toISOString() }
  });
  await indexRecord(result.data.uri);
}

async function removeMatching(agent, collection, predicate) {
  const match = await findMatching(agent, collection, predicate);
  if (!match) return null;
  const rkey = match.uri.split("/").at(-1);
  await agent.com.atproto.repo.deleteRecord({ repo: agent.assertDid, collection, rkey });
  await api("/v1/index/delete", { method: "POST", body: { uri: match.uri } });
  return match;
}

async function findMatching(agent, collection, predicate) {
  let cursor;
  do {
    const response = await agent.com.atproto.repo.listRecords({ repo: agent.assertDid, collection, limit: 100, cursor });
    const match = response.data.records.find(item => predicate(item.value));
    if (match) return match;
    cursor = response.data.cursor;
  } while (cursor);
  return null;
}

async function indexRecord(uri) {
  return api("/v1/index/record", { method: "POST", body: { uri } });
}
